<template>
  <DialogContainer
    title="Edit Activity"
    icon="edit"
    :showDialog="showDialog"
    :toolbarActions="[
      {
        text: 'Save',
        icon: 'save',
        buttonColor: 'success',
        emitEvent: 'onSubmitClicked',
        showIsLoading: true,
        isDisabledFunc: () => !isFormValid,
      },
      {
        text: 'Cancel',
        depressed: true,
        emitEvent: 'onCancelClicked',
      },
    ]"
    @onCancelClicked="onCancelClicked"
    @onSubmitClicked="onSubmitClicked"
    :isLoading="isLoading"
  >
    <v-container pa-0 grid-list-lg>
      <v-form v-model="isFormValid" ref="form">
        <v-layout wrap>
          <v-flex xs12>
            <ControlAutoComplete
              :items="enabledActivityTypeList"
              autocomplete
              item-text="name"
              item-value="id"
              v-model="typeId"
              clearable
              label="Activity Type*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 v-if="type && type.isStatusChange">
            <ControlAutoComplete
              :items="availableAssetStatuses"
              autocomplete
              item-text="name"
              item-value="id"
              v-model="statusId"
              clearable
              label="Equipment Status*"
              :disabled="!typeId || isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 v-if="type && type.isLocationChange">
            <ControlAutoComplete
              :items="enabledLocationList"
              :item-text="
                (location) => {
                  return (
                    '' +
                    location.name +
                    ' - ' +
                    _entityPropertyValueByName({
                      entityList: clientList,
                      idKey: 'id',
                      idValue: location.clientIdOwner,
                      propertyKey: 'name',
                    })
                  );
                }
              "
              item-value="id"
              v-model="locationId"
              clearable
              label="Location*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 md6>
            <ControlDatePicker
              v-model="datePerformedOn"
              label="Activity Date*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 md6>
            <ControlTimePicker
              v-model="timePerformedOn"
              label="Activity Time*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12>
            <ControlAutoComplete
              :items="enabledUserList"
              item-text="name"
              item-value="id"
              v-model="userIdPerformedBy"
              clearable
              label="Performed By*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12>
            <ControlTextArea
              v-model="comments"
              label="Comments"
              :disabled="isLoading"
            />
          </v-flex>
          <v-flex xs12>
            <ControlFileInput
              v-model="files"
              label="Comments"
              :disabled="isLoading"
            />
          </v-flex>
        </v-layout>
        <small>*indicates required field</small>
      </v-form>
    </v-container>
  </DialogContainer>
</template>

<script>
import mixinAssetHelper from '../../../common/mixins/mixinAssetHelper'

import DialogContainer from '../../../common/components/layouts/DialogContainer'
import ControlTextField from '../../../common/components/controls/ControlTextField'
import ControlDatePicker from '../../../common/components/controls/ControlDatePicker'
import ControlTimePicker from '../../../common/components/controls/ControlTimePicker'
import ControlAutoComplete from '../../../common/components/controls/ControlAutoComplete'
import ControlTextArea from '../../../common/components/controls/ControlTextArea'
import ControlFileInput from '../../../common/components/controls/ControlFileInput'

export default {
  components: {
    DialogContainer,
    ControlTextField,
    ControlDatePicker,
    ControlTimePicker,
    ControlAutoComplete,
    ControlTextArea,
    ControlFileInput,
  },
  mixins: [mixinAssetHelper],
  props: {
    showDialog: {
      type: Boolean,
      required: true,
    },
    activityDetail: {
      type: Object,
      required: true,
    },
    modelList: {
      type: Array,
      required: true,
    },
    locationList: {
      type: Array,
      required: true,
    },
    userList: {
      type: Array,
      required: true,
    },
    clientList: {
      type: Array,
      required: true,
    },
    activityTypeList: {
      type: Array,
      required: true,
    },
    assetStatusList: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    isFormValid: false,
    typeId: null,
    datePerformedOn: null,
    timePerformedOn: null,
    userIdPerformedBy: null,
    comments: null,
    locationId: null,
    statusId: null,
    files: null,
    availableActivityTypes: [],
    availableAssetStatuses: [],
  }),
  computed: {
    enabledActivityTypeList() {
      return this.activityTypeList.filter(
        (activityType) => activityType.isEnabled
      )
    },
    enabledLocationList() {
      return this.locationList.filter((location) => location.isEnabled)
    },
    enabledUserList() {
      return this.userList.filter((user) => user.isEnabled)
    },
    type() {
      return this.activityTypeList.find(
        (activityType) => activityType.id == this.typeId
      )
    },
  },
  watch: {
    showDialog() {
      if (this.showDialog && this.activityDetail) {
        this.typeId = this.activityDetail.typeId
        this.userIdPerformedBy = this.activityDetail.userIdPerformedBy

        if (this.activityDetail.dateTimePerformedOn) {
          const { dateExtracted, timeExtracted } = this._getDateParts(
            this.activityDetail.dateTimePerformedOn
          )
          this.datePerformedOn = dateExtracted
          this.timePerformedOn = timeExtracted
        }

        this.comments = this.activityDetail.comments
        this.locationId = this.activityDetail.locationId
        this.statusId = this.activityDetail.statusId
      }
      if (!this.showDialog) {
        this.clearDialog()
      }
    },
    typeId() {
      this.setAvailableAssetStatuses()
    },
  },
  methods: {
    setAvailableAssetStatuses() {
      if (!this.type) {
        this.availableAssetStatuses = []
        return
      }
      this.availableAssetStatuses = this.assetStatusList.filter(
        (assetStatus) =>
          assetStatus.isEnabled &&
          this.type.assetStatusIds.includes(assetStatus.id)
      )
    },
    onSubmitClicked() {
      this.$emit('onSubmitClicked', {
        typeId: this.typeId,
        dateTimeEpochPerformedOn: new Date(
          this.datePerformedOn + ' ' + this.timePerformedOn
        ).valueOf(),
        userIdPerformedBy: this.userIdPerformedBy,
        comments: this.comments,
        locationId: this.locationId,
        statusId: this.statusId,
        files: this.files,
      })
    },
    onCancelClicked() {
      this.$emit('onCancelClicked')
    },
    clearDialog() {
      this.$refs.form.reset()
      this.typeId = null
      this.files = null
      this.datePerformedOn = null
      this.timePerformedOn = null
      this.userIdPerformedBy = null
      this.comments = null
      this.locationId = null
    },
  },
}
</script>